import { FormControlLabel, Grid, Stack, Switch } from "@mui/material";
import { StatCard } from "components/common/StatCard";
import { useTenantTranslation, useUnitsFormatter } from "hooks/formatters";
import { useStringQueryParamState } from "hooks/useStringQueryParamState";
import { useEffect, useState } from "react";
import { SavingsTimeseriesPoint } from "src/store/api/generatedApi";
import { useFormatNumber } from "src/utils/formatNumber";
import { TableHeader } from "../TableHeader";
import { ToggleSelector } from "../ToggleSelector";
import { CumulativeSavingsPlot } from "./CumulativeSavingsPlot";
import { SavingsPlot } from "./SavingsPlot";
import { SavingsTable } from "./SavingsTable";
import { SavingsTabData } from "./savingsHooks";

type ChartView = "cumulative" | "perPeriod";
type TimeAggregation = "weekly" | "monthly";
type SavingsView = "total" | "perTonne";

const CHART_VIEW_LABELS: Record<ChartView, string> = {
  cumulative: "cumulativeSavings",
  perPeriod: "perPeriod",
};

const TIME_AGGREGATION_LABELS: Record<TimeAggregation, string> = {
  monthly: "monthly",
  weekly: "weekly",
};

interface SavingsTabProps {
  data: SavingsTabData;
}

export const SavingsTab = ({ data }: SavingsTabProps) => {
  const { t } = useTenantTranslation();
  const formatNumber = useFormatNumber();
  const units = useUnitsFormatter(false);

  const [chartView, setChartView] = useStringQueryParamState<ChartView>(
    "chartView",
    "cumulative"
  );
  const [selectedAggregation, setSelectedAggregation] =
    useState<TimeAggregation>("monthly");
  const [selectedView, setSelectedView] = useState<SavingsView>("total");
  const [includeAlloy, setIncludeAlloy] = useState(true);
  const hasAlloy = data.alloyMonthly.length > 0;

  const tableAggregation: TimeAggregation =
    chartView === "cumulative" ? "monthly" : selectedAggregation;
  const tableData = tableAggregation === "weekly" ? data.weekly : data.monthly;
  const alloyTableData =
    tableAggregation === "weekly" ? data.alloyWeekly : data.alloyMonthly;

  const [selectedDate, setSelectedDate] = useState<Date | null>(() =>
    getDefaultSelectedDate(tableData)
  );

  useEffect(() => {
    // eslint-disable-next-line @eslint-react/set-state-in-effect -- resets date on aggregation/view change; user also clicks plot
    setSelectedDate(getDefaultSelectedDate(tableData));
    // eslint-disable-next-line @eslint-react/exhaustive-deps -- only reset date when the view or aggregation changes, not on API refetches
  }, [chartView, selectedAggregation]);

  const totalHeats = data.monthly
    .filter((d) => d.standard === "all")
    .reduce((sum, d) => sum + d.heat_count, 0);

  const totalChargedMass = data.monthly
    .filter((d) => d.standard === "all")
    .reduce((sum, d) => sum + d.total_charged_mass, 0);

  const totalAlloyChargedMass = data.monthly
    .filter((d) => d.standard === "all")
    .reduce((sum, d) => sum + d.alloy_charged_mass, 0);

  const cumulativeWithAlloy =
    data.cumulativeSavings + (includeAlloy ? data.cumulativeAlloySavings : 0);
  const denominatorMass =
    totalChargedMass + (includeAlloy ? totalAlloyChargedMass : 0);
  const avgSpecificSavings =
    denominatorMass > 0 ? cumulativeWithAlloy / denominatorMass : 0;

  return (
    <Stack pb={50}>
      <Grid container spacing={2} sx={{ mt: 2 }}>
        <Grid size={{ xs: 4 }}>
          <StatCard
            label={t("cumulativeSavings")}
            value={`${formatNumber(0)(cumulativeWithAlloy)}${units("cost")}`}
            caption={t("sinceDeployment")}
          />
        </Grid>
        <Grid size={{ xs: 4 }}>
          <StatCard
            label={t("averageSavings")}
            value={`${formatNumber(2)(avgSpecificSavings)}${units(
              "specific_cost"
            )}`}
            valueColor={
              avgSpecificSavings > 0
                ? "success.main"
                : avgSpecificSavings < 0
                  ? "error.main"
                  : "text.primary"
            }
            caption={t("sinceDeployment")}
          />
        </Grid>
        <Grid size={{ xs: 4 }}>
          <StatCard
            label={t("scrapChefHeats")}
            value={totalHeats}
            caption={t("totalHeatsDeployedWithScrapChef")}
          />
        </Grid>
      </Grid>
      <Stack direction="row" spacing={2} sx={{ mt: 3 }}>
        <ToggleSelector<ChartView>
          selectedValue={chartView}
          setSelectedValue={setChartView}
          availableValueLabels={CHART_VIEW_LABELS}
        />
        {chartView === "perPeriod" ? (
          <>
            <ToggleSelector<TimeAggregation>
              selectedValue={selectedAggregation}
              setSelectedValue={setSelectedAggregation}
              availableValueLabels={TIME_AGGREGATION_LABELS}
            />
            <ToggleSelector<SavingsView>
              selectedValue={selectedView}
              setSelectedValue={setSelectedView}
              availableValueLabels={{
                total: "Total",
                perTonne: units("specific_cost"),
              }}
            />
          </>
        ) : null}
        {hasAlloy ? (
          <FormControlLabel
            sx={{ alignSelf: "flex-end", ml: "auto" }}
            control={
              <Switch
                size="small"
                checked={includeAlloy}
                onChange={(event) => {
                  setIncludeAlloy(event.target.checked);
                }}
              />
            }
            label={t("includeAlloySavings")}
          />
        ) : null}
      </Stack>
      <Grid container spacing={1} mt={2} mx={0}>
        <Grid size={{ xs: 8 }} ml={0}>
          {chartView === "cumulative" ? (
            <CumulativeSavingsPlot
              data={data.monthly}
              includeAlloy={includeAlloy}
              setSelectedDate={setSelectedDate}
            />
          ) : (
            <SavingsPlot
              data={tableData}
              includeAlloy={includeAlloy}
              setSelectedDate={setSelectedDate}
              showSpecific={selectedView === "perTonne"}
            />
          )}
        </Grid>
        <Grid size={{ xs: 4 }}>
          <TableHeader
            titlePrefix={t("scrapSavingsBreakdown")}
            subtitleText={t("savingsTableSubtitleText")}
            selectedDate={selectedDate}
            formatDateMonthly={tableAggregation === "monthly"}
          />
          <SavingsTable data={tableData} selectedDate={selectedDate} />
          {hasAlloy && includeAlloy ? (
            <Stack sx={{ mt: 3 }}>
              <TableHeader
                titlePrefix={t("alloySavingsBreakdown")}
                subtitleText={t("savingsTableSubtitleText")}
                selectedDate={selectedDate}
                formatDateMonthly={tableAggregation === "monthly"}
              />
              <SavingsTable data={alloyTableData} selectedDate={selectedDate} />
            </Stack>
          ) : null}
        </Grid>
      </Grid>
    </Stack>
  );
};

const getDefaultSelectedDate = (
  data: SavingsTimeseriesPoint[]
): Date | null => {
  const allDates = data
    .filter((d) => d.standard === "all")
    .map((d) => d.period_start)
    .sort();
  const latest = allDates[allDates.length - 1];
  return latest ? new Date(latest) : null;
};
